/**
 * <s-addons> — "Complete your carry" add-on rows on the product page (snippets/product-addons.liquid).
 * Merchant 2026-10-30: compact rows, "+ Add" that turns into a "− n +" stepper in place, image style tiles, "Show all".
 * Each row keeps a selection of styles → quantities, so a shopper can take two of one style or two different styles
 * (rare case, merchant: keep the logic working without growing the rows). The chips line under the name appears only
 * when more than one style is selected. "Details" opens the shared quick view in add-on mode; s-quick-add-modal.js
 * dispatches "addon:select" with the chosen variant, which adds one of it here.
 * The element lives inside the 'product-form' partial and is replaced on every variant change; selections are kept in
 * a module-level Map keyed by product id. s-product-form.js reads lines() on submit, total() for the button price,
 * and calls reset() after success.
 */
const state = new Map();
const expandedState = new Map();
const foldState = new Map();

class SAddons extends HTMLElement {
  #ac;
  #total = 0;
  /** @type {Map<Element, {current: string, items: Map<string, number>}>} */
  #rowState = new Map();

  /** Sum of the selected add-ons in cents. */
  total() {
    return this.#total;
  }

  /**
   * Desktop fold (merchant 2026-10-31): the add-ons sit between Material and Add to cart, collapsed to the header line
   * ([data-addon-collapsed], styles.css). The header toggles them; the open state survives variant changes (foldState).
   * Phones: the header is a plain heading and the rows stay open.
   */
  #setupFold(signal) {
    const toggle = this.querySelector('[data-addon-toggle]');
    if (!toggle) return;
    // Phones fold too since 2026-11-01 (merchant: same as PC, never opened by default).
    const desktop = window.matchMedia('all');
    const list = this.querySelector('[data-addon-list]');
    const apply = () => {
      const open = Boolean(foldState.get(this.dataset.productId));
      this.toggleAttribute('data-addon-collapsed', !open);
      if (desktop.matches) {
        toggle.tabIndex = 0;
        toggle.setAttribute('aria-expanded', String(open));
        if (list?.id) toggle.setAttribute('aria-controls', list.id);
      } else {
        toggle.tabIndex = -1;
        toggle.removeAttribute('aria-expanded');
        toggle.removeAttribute('aria-controls');
      }
    };
    toggle.addEventListener('click', () => {
      if (!desktop.matches) return;
      foldState.set(this.dataset.productId, !foldState.get(this.dataset.productId));
      apply();
    }, { signal });
    desktop.addEventListener('change', apply, { signal });
    apply();
  }

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    for (const row of this.#rows) this.#rowState.set(row, { current: row.dataset.defaultVariant, items: new Map() });
    this.#restore();
    this.#setExpanded(Boolean(expandedState.get(this.dataset.productId)));
    this.#setupFold(signal);

    this.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || !target.matches('[data-addon-style]')) return;
      const row = target.closest('[data-addon-row]');
      const rs = row && this.#rowState.get(row);
      if (!rs) return;
      // A tile only chooses the style. The first pick also adds it (intent); with a selection already made, the
      // button shows "+ Add" (or this style's stepper) so a second style is an explicit tap.
      if (rs.items.size === 0) rs.items.set(target.value, 1);
      this.#select(row, target.value);
      this.#commit();
    }, { signal });

    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const row = target.closest('[data-addon-row]');
      const rs = row && this.#rowState.get(row);

      if (rs && target.closest('[data-addon-add]')) {
        rs.items.set(rs.current, 1);
        this.#commit();
      } else if (rs && target.closest('[data-addon-inc]')) {
        rs.items.set(rs.current, (rs.items.get(rs.current) || 0) + 1);
        this.#commit();
      } else if (rs && target.closest('[data-addon-dec]')) {
        const next = (rs.items.get(rs.current) || 0) - 1;
        if (next > 0) rs.items.set(rs.current, next);
        else rs.items.delete(rs.current);
        this.#commit();
        if (next <= 0) row.querySelector('[data-addon-add]')?.focus();
      } else if (rs && target.closest('[data-addon-remove]')) {
        rs.items.delete(target.closest('[data-addon-remove]').dataset.addonRemove);
        this.#commit();
      } else if (target.closest('[data-addon-more]')) {
        this.#setExpanded(!expandedState.get(this.dataset.productId));
      }
    }, { signal });

    // From the details window: add one of the chosen style.
    document.addEventListener('addon:select', (event) => {
      const { target, variantId } = event.detail || {};
      const row = target ? this.querySelector(`[data-addon-key="${CSS.escape(target)}"]`) : null;
      const rs = row && this.#rowState.get(row);
      if (!rs) return;
      const id = String(variantId || rs.current);
      rs.items.set(id, (rs.items.get(id) || 0) + 1);
      this.#select(row, id);
      if (row.hasAttribute('data-addon-extra')) this.#setExpanded(true);
      this.#commit();
    }, { signal });

    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #rows() {
    return [...this.querySelectorAll('[data-addon-row]')];
  }

  #commit() {
    this.#save();
    this.#render();
  }

  #setExpanded(open) {
    expandedState.set(this.dataset.productId, open);
    for (const row of this.querySelectorAll('[data-addon-extra]')) {
      row.hidden = !open && !(this.#rowState.get(row)?.items.size > 0);
    }
    const more = this.querySelector('[data-addon-more]');
    if (!more) return;
    more.setAttribute('aria-expanded', String(open));
    more.querySelector('[data-addon-more-label]').hidden = open;
    more.querySelector('[data-addon-less-label]').hidden = !open;
  }

  #radio(row, id) {
    return row.querySelector(`[data-addon-style][value="${CSS.escape(String(id))}"]`);
  }

  #styleName(row, id) {
    return this.#radio(row, id)?.closest('label')?.getAttribute('title') || '';
  }

  /** Make `id` the row's current style: tile, photo, price and style name follow it. */
  #select(row, id) {
    const rs = this.#rowState.get(row);
    rs.current = String(id);
    const radio = this.#radio(row, id);
    if (!radio) return;
    radio.checked = true;
    // A style hidden behind "+N" (picked in the details window) becomes visible so the choice shows.
    radio.closest('label')?.classList.remove('hidden');
    const price = row.querySelector('[data-addon-price]');
    if (price && radio.dataset.price) price.dataset.price = radio.dataset.price;
    const name = row.querySelector('[data-addon-style-name]');
    const title = this.#styleName(row, id);
    if (name && title) name.textContent = title;
    if (radio.dataset.image) {
      for (const image of row.querySelectorAll('[data-addon-image]')) {
        image.removeAttribute('srcset');
        image.src = radio.dataset.image;
      }
    }
  }

  #priceOf(row, id) {
    const radio = this.#radio(row, id);
    if (radio?.dataset.price) return Number(radio.dataset.price);
    return Number(row.querySelector('[data-addon-price]')?.dataset.price || 0);
  }

  #save() {
    state.set(this.dataset.productId, this.#rows.map((row) => {
      const rs = this.#rowState.get(row);
      return { current: rs.current, items: [...rs.items] };
    }));
  }

  #restore() {
    const saved = state.get(this.dataset.productId);
    if (!saved) return;
    this.#rows.forEach((row, index) => {
      const entry = saved[index];
      if (!entry) return;
      const rs = this.#rowState.get(row);
      rs.items = new Map(entry.items);
      this.#select(row, entry.current);
    });
  }

  #format(cents) {
    const currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  #renderChips(row, rs) {
    const chips = row.querySelector('[data-addon-chips]');
    const name = row.querySelector('[data-addon-style-name]');
    if (!chips) return;
    const several = rs.items.size > 1 || (rs.items.size === 1 && !rs.items.has(rs.current));
    chips.hidden = !several;
    if (name) name.hidden = several;
    if (!several) {
      chips.replaceChildren();
      return;
    }
    const nodes = [document.createTextNode(this.dataset.labelSelected || '')];
    for (const [id, qty] of rs.items) {
      const title = this.#styleName(row, id);
      const chip = document.createElement('span');
      chip.className = 'inline-flex items-center gap-0.5 text-on-surface';
      chip.append(`${title} ×${qty}`);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.addonRemove = id;
      remove.className = 'inline-flex size-6 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-on-surface-secondary hover:text-on-surface';
      remove.setAttribute('aria-label', (this.dataset.labelRemove || '%S%').replace('%S%', title));
      remove.innerHTML = '<svg class="size-3" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="m4 4 8 8M12 4l-8 8"/></svg>';
      chip.append(remove);
      nodes.push(chip);
    }
    chips.replaceChildren(...nodes);
  }

  #render() {
    let total = 0;
    let count = 0;
    for (const row of this.#rows) {
      const rs = this.#rowState.get(row);
      const price = row.querySelector('[data-addon-price]');
      if (price) price.textContent = this.#format(Number(price.dataset.price || 0));

      for (const [id, qty] of rs.items) {
        total += this.#priceOf(row, id) * qty;
        count += qty;
      }

      const qty = rs.items.get(rs.current) || 0;
      const add = row.querySelector('[data-addon-add]');
      const stepper = row.querySelector('[data-addon-stepper]');
      const hadFocus = stepper?.contains(document.activeElement);
      if (add) add.hidden = qty > 0;
      if (stepper) stepper.hidden = qty === 0;
      const qtyEl = row.querySelector('[data-addon-qty]');
      if (qtyEl) qtyEl.textContent = String(qty);
      if (qty > 0 && add === document.activeElement) row.querySelector('[data-addon-inc]')?.focus();
      if (hadFocus && qty === 0) add?.focus();

      this.#renderChips(row, rs);
    }

    const countLine = this.querySelector('[data-addon-count]');
    if (countLine) countLine.hidden = count > 0;
    const summary = this.querySelector('[data-addon-summary]');
    if (summary) {
      summary.hidden = count === 0;
      const template = count === 1 ? this.dataset.summaryOne : this.dataset.summaryOther;
      summary.textContent = count ? (template || '').replace('{{ count }}', count).replace('{{ total }}', this.#format(total)) : '';
    }

    this.#total = total;
    // s-product-form.js adds this to the main "Add to cart · $…" price.
    this.dispatchEvent(new CustomEvent('addons:change', { bubbles: true, detail: { total, count } }));
  }

  /** Cart lines for every selected add-on style. */
  lines() {
    const lines = [];
    for (const row of this.#rows) {
      for (const [id, qty] of this.#rowState.get(row).items) {
        if (id && qty > 0) lines.push({ merchandiseId: id, quantity: qty });
      }
    }
    return lines;
  }

  reset() {
    state.delete(this.dataset.productId);
    for (const row of this.#rows) this.#rowState.get(row).items.clear();
    this.#render();
  }
}

if (!customElements.get('s-addons')) {
  customElements.define('s-addons', SAddons);
}
